<template lang="html">
  <section v-if="collections && user">
    <div class="container">
      <section class="hero is-light-blue is-small has-bg-img">
        <!-- Hero body -->
        <div class="hero-body">
          <div class="columns">
            <div class="column">
              <div class="columns">
                <div class="column is-1">
                  <figure class="avatar image">
                    <img
                      v-if="!isLoading"
                      style="border-radius: 50%"
                      :src="user.avatar.url"
                      alt=""
                    />
                    <b-skeleton
                      v-if="isLoading"
                      circle
                      width="128px"
                      height="128px"
                    ></b-skeleton>
                  </figure>
                </div>
                <div class="column">
                  <nav class="level is-marginless">
                    <!-- Hero left side -->
                    <div class="level-left">
                      <div class="level-item">
                        <h1 class="title">{{ user.cosplayer_name }}</h1>
                        <country-flag :country="user.country" :rounded="true" />
                      </div>
                    </div>
                    <!-- Hero right side -->
                    <div v-if="isLoggedIn" class="level-right">
                      <!-- Add teacher button -->
                      <div class="level-item" v-if="!owner">
                        <add-teacher-button
                          :key="user.username"
                          :teacher="user"
                          :user="currentUser.attributes.username"
                        />
                      </div>
                      <!-- Reporting -->
                      <div v-if="!owner" class="level-item">
                        <b-dropdown aria-role="list" position="is-bottom-left">
                          <b-button
                            role="button"
                            slot="trigger"
                            class="is-danger is-inverted"
                          >
                            <b-icon size="is-small" icon="block-helper"></b-icon>
                          </b-button>

                          <b-dropdown-item aria-role="listitem"
                            ><b-icon
                              size="is-small"
                              type="is-danger"
                              icon="account-cancel"
                            ></b-icon>
                            Block user</b-dropdown-item
                          >
                          <b-dropdown-item aria-role="listitem"
                            ><b-icon
                              size="is-small"
                              type="is-danger"
                              icon="alert-octagon"
                            ></b-icon>
                            Report user</b-dropdown-item
                          >
                        </b-dropdown>
                      </div>
                    </div>
                  </nav>

                  <p class="is-marginless block">
                    <b-tooltip
                      label="Facebook page"
                      position="is-bottom"
                      type="is-primary"
                      ><a
                        v-if="user.facebook"
                        target="_blank"
                        :href="`https://facebook.com/${user.facebook}`"
                        ><b-icon
                          type="is-dark"
                          icon="facebook"
                          size="is-small"
                        ></b-icon></a
                    ></b-tooltip>
                    <b-tooltip
                      label="Instagram page"
                      position="is-bottom"
                      type="is-primary"
                      ><a
                        v-if="user.instagram"
                        target="_blank"
                        :href="`https://instagram.com/${user.instagram}`"
                        ><b-icon
                          type="is-dark"
                          icon="instagram"
                          size="is-small"
                        ></b-icon></a
                    ></b-tooltip>
                    <b-tooltip label="Youtube page" position="is-bottom" type="is-primary"
                      ><a
                        v-if="user.youtube"
                        target="_blank"
                        :href="`https://youtube.com/channel/${user.youtube}`"
                        ><b-icon
                          type="is-dark"
                          icon="youtube"
                          size="is-small"
                        ></b-icon></a
                    ></b-tooltip>
                    <b-tooltip label="Ko-fi page" position="is-bottom" type="is-primary"
                      ><a
                        v-if="user.kofi"
                        target="_blank"
                        :href="`https://ko-fi.com/${user.kofi}`"
                        ><b-icon type="is-dark" icon="coffee" size="is-small"></b-icon></a
                    ></b-tooltip>
                    <b-tooltip label="Twitter page" position="is-bottom" type="is-primary"
                      ><a
                        v-if="user.twitter"
                        target="_blank"
                        :href="`https://twitter.com/${user.twitter}`"
                        ><b-icon
                          type="is-dark"
                          icon="twitter"
                          size="is-small"
                        ></b-icon></a
                    ></b-tooltip>
                  </p>
                  <span class="is-italic is-size-7 has-text-gray"
                    >Joined {{ dateFromNow(user.created_at) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="section">
        <div class="container">
          <!-- Navigation -->
          <nav class="breadcrumb" aria-label="breadcrumbs">
            <ul>
              <li>
                <router-link
                  aria-current="page"
                  :to="{ path: `/users/${user.username}` }"
                  >{{ user.cosplayer_name }}</router-link
                >
              </li>
              <li class="is-active"><a href="#" aria-current="page">Collections</a></li>
            </ul>
          </nav>

          <div class="columns">
            <div class="column is-2">
              <b-menu>
                <b-menu-list label="Collections">
                  <div v-for="collection in collections.slice(0, 1)" :key="collection.id">
                    <b-menu-item
                      :active="selectedCollection == collection"
                      @click="selectCollection(collection)"
                    >
                      <template slot="label">
                        <span class="ellipsis">
                          <b-icon size="is-small" :icon="typeIcon(collection)" />
                          {{ collection.attributes.name }}</span
                        >
                      </template>
                    </b-menu-item>
                  </div>
                  <div v-for="collection in collections.slice(1)" :key="collection.id">
                    <b-menu-item
                      :active="selectedCollection == collection"
                      @click="selectCollection(collection)"
                    >
                      <template slot="label">
                        <span class="ellipsis">
                          <b-icon size="is-small" :icon="typeIcon(collection)" />
                          {{ collection.attributes.name }}</span
                        >
                      </template>
                    </b-menu-item>
                  </div>
                </b-menu-list>
                <b-menu-list>
                  <b-menu-item
                    v-if="isLoggedIn && owner"
                    @click="newCollectionModalOpen = true"
                    icon="plus"
                    label="Create a Collection"
                  ></b-menu-item>
                </b-menu-list>
              </b-menu>
            </div>

            <!-- Collections -->
            <div class="column is-10">
              <collection
                v-if="selectedCollection"
                :collection="selectedCollection"
                :collections="collections"
                @collectionEdited="assignCollections"
                @collectionRemoved="assignResetCollections"
                @collectionsUpdated="assignCollections"
              />
            </div>
          </div>

          <!-- Loaders and modals -->
          <b-loading :is-full-page="true" :active.sync="isLoading"></b-loading>

          <b-modal
            :active.sync="newCollectionModalOpen"
            has-modal-card
            trap-focus
            aria-role="dialog"
            aria-modal
          >
            <new-collection @collectionCreated="assignCollections" />
          </b-modal>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { dateMixin } from '@/mixins/dateMixin'
import { backendMixin } from '@/mixins/backendMixin'

import NewCollection from './NewCollection'
import Collection from './Collection.vue'
import CountryFlag from 'vue-country-flag'
import AddTeacherButton from '@/components/buttons/AddTeacherButton'

export default {
  mixins: [dateMixin, backendMixin],
  data () {
    return {
      isLoading: false,
      newCollectionModalOpen: false,
      user: null,
      collections: [],
      selectedCollection: null
    }
  },
  created () {
    this.fetchData()
  },
  computed: {
    ...mapGetters({
      isOwner: 'isOwner',
      isLoggedIn: 'isLoggedIn',
      currentUser: 'currentUser'
    }),
    owner () {
      return this.isOwner(this.user.id)
    }
  },
  components: {
    NewCollection,
    Collection,
    CountryFlag,
    AddTeacherButton
  },
  methods: {
    fetchData () {
      this.isLoading = true
      this.$store
        .dispatch('users/show', this.$route.params.username)
        .then((resp) => {
          this.user = resp.data.data.attributes
          this.$store
            .dispatch('users/collections/index', this.$route.params.username)
            .then((resp) => {
              this.collections = resp.data.data
              this.initCollection()
              this.isLoading = false
            })
            .catch((_) => {})
          this.isLoading = false
        })
        .catch((error) => {
          this.isLoading = false
          this.handleError(error, this.$route)
        })
    },
    assignCollections (values) {
      this.collections = values
      this.selectedCollection = this.collections.find(
        (col) => col.id === this.selectedCollection.id
      )
    },
    assignResetCollections (values) {
      this.collections = values
      this.selectCollection(this.collections[0])
    },
    initCollection () {
      if (this.$route.query.collection) {
        let desiredCollection = this.collections.find(
          (col) => col.id === this.$route.query.collection
        )
        if (desiredCollection) this.selectCollection(desiredCollection)
      } else {
        this.selectCollection(this.collections[0])
      }
    },
    selectCollection (collection) {
      this.selectedCollection = collection
      this.$router.push({ query: { collection: collection.id } }).catch((_) => {})
    },
    typeIcon (collection) {
      if (!collection.attributes.deletable) return 'heart-outline'
      else if (collection.attributes.hidden) return 'lock'
      else return 'earth'
    }
  }
}
</script>

<style lang="scss" scoped>
.card-header-title.attribute {
  font-weight: normal;
}

.has-bg-img {
  background: linear-gradient(
      to right,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.7),
      rgba(255, 255, 255, 0.4),
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0.4),
      rgba(255, 255, 255, 0.7),
      rgba(255, 255, 255, 1)
    ),
    url("https://costuto-public.s3.eu-central-1.amazonaws.com/images/colors/primary2_hero.png")
      no-repeat center center fixed;

  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;
}
</style>
